import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  DatePicker,
  Select,
  Input,
  message,
  Modal,
  Descriptions,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import logService from '../../services/logService';

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function OrderLogs() {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: null,
    order_type: null,
    product_type: null,
    stage: null,
    user_id: null,
    invoice_id: null,
    subscription_id: null,
    dateRange: null
  });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        status: filters.status,
        order_type: filters.order_type,
        product_type: filters.product_type,
        stage: filters.stage,
        user_id: filters.user_id,
        invoice_id: filters.invoice_id,
        subscription_id: filters.subscription_id
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await logService.getOrderLogs(params);

      if (response.success) {
        setLogs(response.data);
        setPagination({
          ...pagination,
          total: response.meta.total
        });
      } else {
        message.error(response.data.error || '加载失败');
      }
    } catch (error) {
      console.error('Failed to load order logs:', error);
      message.error('加载订单日志失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await logService.getStats({ hours: 24 });

      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await logService.getOrderLog(record.id);

      if (response.success) {
        setSelectedLog(response.data);
        setDetailModalVisible(true);
      } else {
        message.error('加载详情失败');
      }
    } catch (error) {
      message.error('加载详情失败');
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      success: { color: 'success', icon: <CheckCircleOutlined />, text: '成功' },
      failed: { color: 'error', icon: <CloseCircleOutlined />, text: '失败' },
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: '待处理' }
    };

    const config = statusConfig[status] || { color: 'default', icon: null, text: status };

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '订单类型',
      dataIndex: 'order_type',
      key: 'order_type',
      width: 120,
      render: (type) => type || '-'
    },
    {
      title: '产品类型',
      dataIndex: 'product_type',
      key: 'product_type',
      width: 100,
      render: (type) => {
        const typeConfig = {
          dns: { color: 'blue', text: 'DNS' },
          cdn: { color: 'green', text: 'CDN' },
          domain: { color: 'purple', text: 'Domain' }
        };
        const config = typeConfig[type] || { color: 'default', text: type };
        return type ? <Tag color={config.color}>{config.text}</Tag> : '-';
      }
    },
    {
      title: '阶段',
      dataIndex: 'stage',
      key: 'stage',
      width: 100
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
      render: (id) => id || '-'
    },
    {
      title: 'Invoice ID',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
      width: 100,
      render: (id) => id || '-'
    },
    {
      title: 'Subscription ID',
      dataIndex: 'subscription_id',
      key: 'subscription_id',
      width: 120,
      render: (id) => id || '-'
    },
    {
      title: '耗时(秒)',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => duration ? Number(duration).toFixed(2) : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      )
    }
  ];

  return (
    <div>
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总数 (24h)"
                value={stats.orders.total}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="成功"
                value={stats.orders.success}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="失败"
                value={stats.orders.failed}
                valueStyle={{ color: '#f5222d' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="待处理"
                value={stats.orders.pending}
                valueStyle={{ color: '#999' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card
        title="订单处理日志"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadLogs}>
              刷新
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="状态"
            style={{ width: 120 }}
            allowClear
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Option value="success">成功</Option>
            <Option value="failed">失败</Option>
            <Option value="pending">待处理</Option>
          </Select>

          <Select
            placeholder="订单类型"
            style={{ width: 150 }}
            allowClear
            value={filters.order_type}
            onChange={(value) => setFilters({ ...filters, order_type: value })}
          >
            <Option value="subscription">订阅</Option>
            <Option value="domain_registration">域名注册</Option>
            <Option value="domain_renewal">域名续费</Option>
          </Select>

          <Select
            placeholder="产品类型"
            style={{ width: 120 }}
            allowClear
            value={filters.product_type}
            onChange={(value) => setFilters({ ...filters, product_type: value })}
          >
            <Option value="dns">DNS</Option>
            <Option value="cdn">CDN</Option>
            <Option value="domain">Domain</Option>
          </Select>

          <Select
            placeholder="阶段"
            style={{ width: 120 }}
            allowClear
            value={filters.stage}
            onChange={(value) => setFilters({ ...filters, stage: value })}
          >
            <Option value="payment">支付</Option>
            <Option value="activation">激活</Option>
            <Option value="provision">配置</Option>
            <Option value="queue">队列</Option>
          </Select>

          <Input
            placeholder="用户ID"
            style={{ width: 100 }}
            allowClear
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            prefix={<SearchOutlined />}
          />

          <Input
            placeholder="Invoice ID"
            style={{ width: 120 }}
            allowClear
            value={filters.invoice_id}
            onChange={(e) => setFilters({ ...filters, invoice_id: e.target.value })}
            prefix={<SearchOutlined />}
          />

          <Input
            placeholder="Subscription ID"
            style={{ width: 140 }}
            allowClear
            value={filters.subscription_id}
            onChange={(e) => setFilters({ ...filters, subscription_id: e.target.value })}
            prefix={<SearchOutlined />}
          />

          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            }
          }}
          scroll={{ x: 1600 }}
        />
      </Card>

      <Modal
        title="订单处理详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="订单类型">{selectedLog.order_type || '-'}</Descriptions.Item>
            <Descriptions.Item label="产品类型">{selectedLog.product_type || '-'}</Descriptions.Item>
            <Descriptions.Item label="阶段">{selectedLog.stage || '-'}</Descriptions.Item>
            <Descriptions.Item label="操作">{selectedLog.action || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(selectedLog.status)}
            </Descriptions.Item>
            <Descriptions.Item label="用户ID">{selectedLog.user_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="管理员ID">{selectedLog.admin_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Invoice ID">{selectedLog.invoice_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Subscription ID">{selectedLog.subscription_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="IP地址">{selectedLog.ip_address || '-'}</Descriptions.Item>
            <Descriptions.Item label="重试次数">{selectedLog.retry_count || 0}</Descriptions.Item>
            <Descriptions.Item label="耗时">
              {selectedLog.duration ? `${Number(selectedLog.duration).toFixed(2)}秒` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {selectedLog.created_at ? dayjs(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {selectedLog.updated_at ? dayjs(selectedLog.updated_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            {selectedLog.retry_at && (
              <Descriptions.Item label="下次重试时间">
                {dayjs(selectedLog.retry_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            )}
            {selectedLog.message && (
              <Descriptions.Item label="消息">
                {selectedLog.message}
              </Descriptions.Item>
            )}
            {selectedLog.data && (
              <Descriptions.Item label="数据">
                <pre style={{ maxHeight: 200, overflow: 'auto', background: '#f5f5f5', padding: 8 }}>
                  {typeof selectedLog.data === 'string' ? selectedLog.data : JSON.stringify(selectedLog.data, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.exception && (
              <Descriptions.Item label="异常">
                <pre style={{ maxHeight: 200, overflow: 'auto', background: '#fff1f0', padding: 8, color: '#cf1322' }}>
                  {selectedLog.exception}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.stack_trace && (
              <Descriptions.Item label="堆栈跟踪">
                <pre style={{ maxHeight: 300, overflow: 'auto', background: '#f5f5f5', padding: 8, fontSize: 12 }}>
                  {selectedLog.stack_trace}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
